import { forwardRef, useMemo } from 'react';
import DOMPurify from 'dompurify';
import { sanitizeStandardSection } from './renderHtml';
import {
    PencilSimpleIcon,
    TrashIcon,
    EyeSlashIcon,
    EyeIcon,
    DotsThreeIcon,
    PencilLineIcon,
    ArrowUUpLeftIcon,
    CaretDownIcon,
    PushPinIcon,
    InfoIcon,
} from '@phosphor-icons/react';
import { SortableItem, IconButton, Dropdown, Tooltip, Chip } from '@origo/origo-ui';
import { useSeksjonelementer, getSectionPosition } from '@origo/seksjon';
import type { Seksjon } from '../types';
import type { SectionElement } from './Gallery/types';
import { ArcGisImage } from './Gallery/ArcGisImage';
import { buildImageUrl } from './Gallery/resolveGalleryUri';
import styles from './SectionItem.module.css';
const KOMMUNALT_BILDE_PREFIX = '__kommunalt_bilde:';

interface SectionItemProps {
    seksjon: Seksjon;
    index: number;
    style?: React.CSSProperties;
    expanded?: boolean;
    onExpandedChange?: (expanded: boolean) => void;
    dragHandleProps?: React.HTMLAttributes<HTMLDivElement>;
    isDragging?: boolean;
    onEdit: (seksjon: Seksjon) => void;
    onUpdate: (seksjon: Seksjon) => void;
    onDelete: (id: string) => void;
    onToggleOverride?: (globalId: string, isInherited: boolean, override: boolean) => void;
    onHide?: (globalId: string) => void;
    onUnhide?: (hideObjectId: number) => void;
    isImported?: boolean;
}

/** Human-readable source label for inherited sections */
function sourceLabel(seksjon: Seksjon): string {
    if (seksjon.kommunal) return 'Arvet fra kommunen';
    if (seksjon.isInherited) return 'Arvet';
    return '';
}

export const SectionItem = forwardRef<HTMLDivElement, SectionItemProps>(function SectionItem({
    seksjon,
    index,
    style,
    expanded,
    onExpandedChange,
    dragHandleProps,
    isDragging,
    onEdit,
    onUpdate,
    onDelete,
    onToggleOverride,
    onHide,
    onUnhide,
    isImported,
}, ref) {
    // Element-lasting er opt-in: TanStack queryen aktiveres kun når seksjonen
    // er ekspandert OG har en globalId. Mappen fra domain `SectionElement`
    // til den lokale visningstypen skjer i-komponent siden URL-en er
    // miljø-spesifikk og ikke tilhører domain-typen.
    const elementsQuery = useSeksjonelementer(
        expanded && seksjon.globalId ? seksjon.globalId : undefined,
    );
    const elements = useMemo<SectionElement[]>(
        () =>
            (elementsQuery.data ?? []).map((el) => {
                const rawBeskrivelse = el.beskrivelse ?? '';
                let imageUrl = '';
                let description = rawBeskrivelse;

                let _persistUri: string | undefined;
                if (rawBeskrivelse.startsWith(KOMMUNALT_BILDE_PREFIX)) {
                    const rest = rawBeskrivelse.slice(KOMMUNALT_BILDE_PREFIX.length);
                    const nl = rest.indexOf('\n');
                    _persistUri = nl >= 0 ? rest.slice(0, nl) : rest;
                    imageUrl = _persistUri;
                    description = nl >= 0 ? rest.slice(nl + 1) : '';
                } else {
                    imageUrl = buildImageUrl(el.filId);
                }

                return {
                    id: el.objectId,
                    fil_id: Number(el.filId ?? 0),
                    title: el.tittel ?? '',
                    imageUrl,
                    description,
                    prioritet: el.prioritet,
                    SeksjonsGlobalID: seksjon.globalId,
                    _persistUri,
                };
            }),
        [elementsQuery.data, seksjon.globalId],
    );

    const isHidden = seksjon.hideObjectId != null || !seksjon.synlig;
    // Editability is strictly gated on "belongs to the current context".
    // An inherited record — even if its Overstyrer flag is set at an ancestor
    // level — must go through "Tilpass for dette området" to create a new
    // override at this level; editing it directly would mutate the parent.
    const canEdit = !seksjon.isInherited;
    const isOwnOverride = !seksjon.isInherited && Boolean(seksjon.overstyrer);
    const isInheritedOverride = Boolean(seksjon.isInherited) && Boolean(seksjon.overstyrer);
    const isInheritedPlain = Boolean(seksjon.isInherited) && !seksjon.overstyrer;
    const canOverride = Boolean(seksjon.canToggleOverride && onToggleOverride && seksjon.globalId);
    const isInfoboks = Boolean(seksjon.infoboks);
    // Plassering kommer fra sentral helper: kommunale er alltid festet (topp
    // når topp ∈ {true, undefined}, bunn når topp === false). Ikke-kommunale
    // får aldri 'top'/'bottom' her — kun drag-sortert via prioritet.
    const sectionPosition = getSectionPosition(seksjon);
    const isPinnedTop = sectionPosition === 'top';
    const isPinnedBottom = sectionPosition === 'bottom';
    const isPinned = isPinnedTop || isPinnedBottom;

    /* ---- Status chip as dropdown trigger ---- */
    const statusChipDropdownItems: {
        id: string;
        label: string;
        icon: React.ReactNode;
        onClick: () => void;
    }[] = [];

    if (canOverride && seksjon.isInherited) {
        // Applies to both plain-inherited and inherited-overridden sections:
        // in either case the editable record must live at the current level.
        statusChipDropdownItems.push({
            id: 'override',
            label: 'Tilpass for dette området',
            icon: <PencilLineIcon size={16} />,
            onClick: () => onToggleOverride!(seksjon.globalId!, true, true),
        });
    } else if (canOverride && isOwnOverride) {
        statusChipDropdownItems.push({
            id: 'revert',
            label: 'Tilbakestill til arvet versjon',
            icon: <ArrowUUpLeftIcon size={16} />,
            onClick: () => onToggleOverride!(seksjon.globalId!, false, false),
        });
    }

    const statusChip = (() => {
        // Felles render — chip med valgfri dropdown av sekundærhandlinger.
        // variant 'muted' = rolig (skjul-tilstander); 'warning' = tilpasset.
        // Rød reserveres til destruktivt (Slett), ikke til normale skjul.
        const renderChip = (
            label: string,
            opts: {
                variant?: 'muted' | 'warning';
                icon?: React.ReactNode;
                tooltip?: string;
            } = {},
        ) => {
            const { variant, icon, tooltip } = opts;
            const variantClass =
                variant === 'muted'
                    ? ` ${styles.statusMuted}`
                    : variant === 'warning'
                      ? ` ${styles.statusWarning}`
                      : '';
            // Klikkbart merke (dropdown): handlingene formidles av caret-en,
            // og tooltip ville kollidere med hover-å-åpne. Plain merke pakkes i
            // Tooltip når en forklaring finnes.
            if (statusChipDropdownItems.length > 0) {
                return (
                    <Dropdown
                        trigger={
                            <span className={`${styles.statusChip}${variantClass} ${styles.statusClickable}`}>
                                {icon}
                                {label} <CaretDownIcon size={12} />
                            </span>
                        }
                        groups={[{ items: statusChipDropdownItems }]}
                        align="end"
                        className={styles.statusDropdown}
                    />
                );
            }
            const chip = (
                <span className={`${styles.statusChip}${variantClass}`}>
                    {icon}
                    {label}
                </span>
            );
            return tooltip ? (
                <Tooltip content={tooltip} position="top">
                    {chip}
                </Tooltip>
            ) : (
                chip
            );
        };

        const hiddenIcon = <EyeSlashIcon size={12} aria-hidden="true" />;

        if (isHidden) {
            // Skjult for innbyggeren — enten et eget punkt eller et arvet punkt
            // skjult lokalt her. Tooltip forklarer hvordan man viser det igjen.
            return renderChip('Skjult for innbyggerne', {
                variant: 'muted',
                icon: hiddenIcon,
                tooltip: seksjon.isInherited
                    ? 'Et arvet punkt som er skjult her. Vis det igjen fra «…»-menyen for at innbyggeren skal se det.'
                    : 'Dette punktet vises ikke for innbyggeren. Vis det igjen fra «…»-menyen for å publisere det.',
            });
        }
        if (seksjon.isHiddenByAncestor) {
            return renderChip('Skjult for innbyggerne (arvet)', {
                variant: 'muted',
                icon: hiddenIcon,
                tooltip: 'Skjult på et overordnet nivå. Kan ikke vises herfra — gå til nivået som eier punktet.',
            });
        }
        if (isOwnOverride) {
            return renderChip('Tilpasset', {
                variant: 'warning',
                tooltip: 'Tilpasset for dette området. Avviker fra den arvede versjonen.',
            });
        }
        if (isInheritedOverride || isInheritedPlain) {
            // Surface that a parent has already customized this section so the
            // caseworker understands they'll further-override on top of it.
            const label = isInheritedOverride ? 'Arvet (tilpasset over)' : sourceLabel(seksjon);
            return renderChip(label, {
                tooltip: isInheritedOverride
                    ? 'Arvet fra et overordnet nivå som allerede har tilpasset punktet. Du kan tilpasse videre her.'
                    : 'Arvet fra et overordnet nivå. Vises som det er med mindre du tilpasser det her.',
            });
        }
        return null;
    })();

    /* ---- Overflow dropdown: secondary actions ---- */
    const dropdownItems = [];

    // Pin-actions kun for kommunale: veksle mellom topp og bunn. Ikke-kommunale
    // har ingen pin-tilstand — rekkefølgen styres via drag-sortering på prioritet.
    if (canEdit && seksjon.kommunal) {
        if (!isPinnedTop) {
            dropdownItems.push({
                id: 'pin-top',
                label: 'Fest til toppen',
                icon: <PushPinIcon size={16} />,
                onClick: () => onUpdate({ ...seksjon, topp: true }),
            });
        } else {
            dropdownItems.push({
                id: 'pin-bottom',
                label: 'Fest til bunnen',
                icon: <PushPinIcon size={16} style={{ transform: 'rotate(180deg)' }} />,
                onClick: () => onUpdate({ ...seksjon, topp: false }),
            });
        }
    }

    if (seksjon.canHide && !isHidden && onHide && seksjon.globalId) {
        dropdownItems.push({
            id: 'hide',
            label: 'Skjul for innbygger',
            icon: <EyeSlashIcon size={16} />,
            onClick: () => onHide(seksjon.globalId!),
        });
    }

    if (isHidden && onUnhide && seksjon.hideObjectId != null) {
        dropdownItems.push({
            id: 'unhide',
            label: 'Vis igjen',
            icon: <EyeIcon size={16} />,
            onClick: () => onUnhide(seksjon.hideObjectId!),
        });
    } else if (!seksjon.synlig && seksjon.hideObjectId == null && canEdit) {
        dropdownItems.push({
            id: 'unhide-direct',
            label: 'Vis igjen',
            icon: <EyeIcon size={16} />,
            onClick: () => onUpdate({ ...seksjon, synlig: true }),
        });
    }

    if (canEdit && seksjon.kommunal) {
        dropdownItems.push({
            id: 'toggle-infoboks',
            label: isInfoboks ? 'Gjør om til vanlig punkt' : 'Gjør om til informasjonspunkt',
            icon: <InfoIcon size={16} />,
            onClick: () => onUpdate({ ...seksjon, infoboks: !seksjon.infoboks }),
        });
    }

    if (!seksjon.isInherited) {
        dropdownItems.push({
            id: 'delete',
            label: 'Slett',
            icon: <TrashIcon size={16} />,
            danger: true,
            onClick: () => onDelete(String(seksjon.objectId)),
        });
    }

    const actions = (
        <>
            {canEdit && (
                <IconButton
                    icon={<PencilSimpleIcon size={16} />}
                    label="Rediger"
                    size="sm"
                    onClick={() => onEdit(seksjon)}
                />
            )}
            {dropdownItems.length > 0 && (
                <Dropdown
                    trigger={<DotsThreeIcon size={16} weight="bold" />}
                    groups={[{ items: dropdownItems }]}
                    align="end"
                    className={styles.overflowMenu}
                />
            )}
        </>
    );

    const importedChip = isImported ? (
        <Chip variant="info">Importert</Chip>
    ) : null;

    /* ---- Expandable content: description + gallery ---- */
    const expandableContent = (
        <div className={styles.expandedContent}>
            {seksjon.tekst && (
                <div
                    className={styles.description}
                    dangerouslySetInnerHTML={{ __html: sanitizeStandardSection(seksjon.tekst) }}
                />
            )}
            {seksjon.globalId && (
                <div className={styles.galleryRow}>
                    {elementsQuery.isSuccess && elements.length === 0 && (
                        <p className={styles.galleryEmpty}>Ingen illustrasjoner</p>
                    )}
                    {[...elements]
                        .sort((a, b) => a.prioritet - b.prioritet)
                        .map(el => (
                            <figure key={el.id} className={styles.galleryFigure}>
                                {el.imageUrl && (
                                    !!el._persistUri
                                        ? <ArcGisImage src={el.imageUrl} alt={el.title} className={styles.galleryThumb} />
                                        : <img src={el.imageUrl} alt={el.title} className={styles.galleryThumb} />
                                )}
                                <figcaption className={styles.galleryCaption}>
                                    {el.title && <p className={styles.galleryTitle}>{el.title}</p>}
                                    {el.description && (
                                        <div
                                            className={styles.galleryDescription}
                                            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(el.description) }}
                                        />
                                    )}
                                </figcaption>
                            </figure>
                        ))}
                </div>
            )}
        </div>
    );

    /* ---- Item class for infoboks visual treatment ---- */
    const itemClassName = isInfoboks ? styles.infoboks : undefined;

    // Any inherited section (with or without an inherited override flag) must
    // be lock-positioned — re-ordering an inherited record is meaningless at
    // the current level; order belongs to the level that owns the record.
    const isInheritedAtThisLevel = isInheritedPlain || isInheritedOverride;

    return (
        <div ref={ref} style={style} className={itemClassName}>
            <SortableItem
                index={index}
                showIndex
                title={seksjon.tittel}
                locked={isInheritedAtThisLevel && !isPinned}
                lockedLabel="Arvet — kan ikke flyttes. Åpne for å tilpasse."
                pinPosition={isPinnedTop ? "top" : isPinnedBottom ? "bottom" : undefined}
                pinLabel={isPinnedTop ? "Vises øverst for innbygger" : isPinnedBottom ? "Vises nederst for innbygger" : undefined}
                dragHandleProps={seksjon.isInherited ? undefined : dragHandleProps}
                isDragging={isDragging}
                expanded={expanded}
                onExpandedChange={onExpandedChange}
                meta={(statusChip || importedChip) ? <>{statusChip}{importedChip}</> : undefined}
                actions={actions}
            >
                {expandableContent}
            </SortableItem>
        </div>
    );
});
